import { BreakpointObserver } from '@angular/cdk/layout';
import { effect, inject, isDevMode } from '@angular/core';
import { EventType, Router } from '@angular/router';
import { patchState, signalStore, type, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { eventGroup, on, withEventHandlers, withReducer } from '@ngrx/signals/events';
import { distinctUntilChanged, EMPTY, filter, fromEvent, map, startWith, tap } from 'rxjs';
import { screenSizes, TW_BREAKPOINTS, type ScreenSize } from '../common/tw-theme';
import { isBrowser, isServer } from '../utilities/platform-type';
import { SEOService } from '../services/seo';

export const uiEvents = eventGroup({
  source: 'ui',
  events: {
  }
});

export const sidebarEvents = eventGroup({
  source: 'ui-sidebar',
  events: {
    show: type<void>(),
    hide: type<void>(),
    toggleVisibility: type<void>(),
    open: type<void>(),
    close: type<void>(),
    toggleExpansion: type<void>(),
  }
});

export const UIStore = signalStore(
  { providedIn: 'root' },
  withState({
    layout: 'minimal' as LayoutId,
    navigating: true,
    showSplashScreen: true,
    screenSize: 'xs' as ScreenSize,
    sidebar: {
      visible: false,
      expanded: false
    }
  }),
  withComputed(() => {
    const colorSchemeStore = inject(ColorSchemeStore);

    return {
      colorScheme: colorSchemeStore.resolved
    };
  }),
  withReducer(
    on(sidebarEvents.open, (_, state) => {
      return {
        sidebar: {
          ...state.sidebar,
          expanded: true
        }
      };
    }),
    on(sidebarEvents.close, (_, state) => {
      return {
        sidebar: {
          ...state.sidebar,
          expanded: false
        }
      };
    }),
    on(sidebarEvents.toggleExpansion, (_, state) => {
      return {
        sidebar: {
          ...state.sidebar,
          expanded: !state.sidebar.expanded
        }
      };
    }),
    on(sidebarEvents.show, (_, state) => {
      return {
        sidebar: {
          ...state.sidebar,
          visible: true
        }
      };
    }),
    on(sidebarEvents.hide, (_, state) => {
      return {
        sidebar: {
          ...state.sidebar,
          visible: false
        }
      };
    }),
    on(sidebarEvents.toggleVisibility, (_, state) => {
      return {
        sidebar: {
          ...state.sidebar,
          visible: !state.sidebar.visible
        }
      };
    })
  ),
  withEventHandlers((store) => {
    return {
      trackNavigationState: (() => {
        const router = inject(Router);
        return router.events.pipe(
          map(event => {
            switch (event.type) {
              case EventType.NavigationStart:
              case EventType.ResolveStart: return true;
              case EventType.NavigationEnd:
              case EventType.NavigationCancel:
              case EventType.NavigationError: return false;
              default: return undefined;
            }
          }),
          filter((nav): nav is boolean => typeof nav === 'boolean'),
          distinctUntilChanged(),
          tap(navigating => patchState(store, { navigating })));
      })(),
      watchRouterSEO: (() => {
        const router = inject(Router);
        const sEOService = inject(SEOService);

        return router.events.pipe(
          filter(e => e.type === EventType.ActivationEnd),
          tap(e => {
            {
              const snap = e.snapshot;
              const data = snap.data?.['seo'];

              sEOService.setTitle(data?.title);
              sEOService.setDescription(data?.description);
            }
          })
        );
      })(),
      updateBreakpoint: isBrowser() ?
        (() => {
          const screens = inject(TW_BREAKPOINTS);
          const bo = inject(BreakpointObserver);

          const breakpointQueries = screenSizes.map(size => `(min-width: ${screens[size]})`);

          return bo.observe(breakpointQueries)
            .pipe(
              map(observed => {
                // Find the largest matching breakpoint by iterating backwards
                for (let i = screenSizes.length - 1; i >= 0; i--) {
                  if (observed.breakpoints[breakpointQueries[i]] === true) {
                    return screenSizes[i];
                  }
                }
                return 'xs' satisfies ScreenSize;
              }),
              distinctUntilChanged(),
              tap(size => patchState(store, { screenSize: size })),
            );
        })()
        : EMPTY,
      handleActivation: (() => {
        const router = inject(Router);

        return router.events.pipe(
          filter(e => e.type === EventType.ActivationStart),
          tap(e => {
            const snap = e.snapshot;
            {
              const layout = snap.data?.['layout'] || 'default';
              patchState(store, { layout });
            }
          })
        );
      })()
    };
  }),
  withHooks({
    onInit: (store) => {
      const router = inject(Router);

      if (isBrowser() && isDevMode()) {
        const screens = inject(TW_BREAKPOINTS);
        const themeSizes = Object.keys(screens);
        validateScreenDeclarations(themeSizes, screenSizes);

        function validateScreenDeclarations(
          tailwind: readonly string[],
          screens: readonly string[]
        ) {

          const sets = {
            screens: new Set(screens),
            tailwind: new Set(tailwind),
          };

          for (const size of tailwind) {
            if (!sets.screens.has(size)) {
              throw new Error(`'${size}' is declared in tailwind config, but not declared in typescript screens`);
            }
          }

          for (const size of screens) {
            if (!sets.tailwind.has(size)) {
              throw new Error(`'${size}' is declared in typescript screen, but not declared in tailwind config`);
            }
          }
        }
      }

      // hideSplashScreen
      if (isBrowser()) {
        const eff = effect(() => {
          const navigated = !!router.lastSuccessfulNavigation();
          if (navigated) {
            eff.destroy();
            patchState(store, { showSplashScreen: false });
          }
        });
      }
    }
  }),
);

export type LayoutId = 'default' | 'minimal' | 'full';

export type ColorScheme = 'light' | 'dark';

const COLOR_SCHEME_KEY = 'color-scheme';

export const ColorSchemeStore = signalStore(
  { providedIn: 'root' },
  withState({
    system: 'light' as ColorScheme,
    preference: null as ColorScheme | null
  }),
  withComputed(store => {
    return {
      resolved: () => {
        const preference = store.preference();
        if (preference) return preference;

        return store.system();
      },
      style: () => {
        const preference = store.preference();
        if (preference) return preference;
        return 'light';
      }
    };
  }),
  withMethods((store) => {
    return {
      setPreference: (scheme: ColorScheme | null) => {
        if (scheme)
          localStorage.setItem(COLOR_SCHEME_KEY, scheme);
        else
          localStorage.removeItem(COLOR_SCHEME_KEY);
        patchState(store, { preference: scheme });
      }
    };
  }),
  withEventHandlers((store) => {
    const bo = inject(BreakpointObserver);

    return {
      readSystemState: bo.observe('(prefers-color-scheme: dark)')
        .pipe(map(result => {
          patchState(store, {
            system: result.matches ? 'dark' : 'light'
          });
        })),
      readBrowserState: isBrowser() ?
        fromEvent<StorageEvent>(window, 'storage')
          .pipe(
            filter(event => event.storageArea === localStorage && event.key === COLOR_SCHEME_KEY),
            map(event => event.newValue),
            startWith(localStorage.getItem(COLOR_SCHEME_KEY)),
            map(scheme => {
              if (scheme !== 'dark' && scheme !== 'light')
                scheme = null;
              patchState(store, { preference: scheme });
            }))
        : EMPTY,
    };
  }),
  withHooks({
    onInit: (store) => {
      if (isServer())
        return;

      effect(() => {
        const scheme = store.style();
        const list = document.documentElement.classList;

        list.toggle('scheme-light', scheme === 'light');
        list.toggle('scheme-dark', scheme === 'dark');
        // list.toggle('scheme-light-dark', scheme === 'light dark');
      });
    }
  })
);

